import navigateToURL from 'lib/navigateToURL';
import EnzymeList from 'molbio/enzymes/backbone/EnzymeList';
import EnzymeListsView from 'molbio/enzymes/EnzymeListsView';
import FeatureLibrariesView from 'molbio/feature-libraries/FeatureLibrariesView';
import {Model as FeatureLibrary} from 'molbio/feature-libraries/FeatureLibrary';
import {Model as Ladder} from 'molbio/virtual-digests/Ladders';
import LaddersView from 'molbio/virtual-digests/LaddersView';
import DatadogUtil from 'performance/DatadogUtil';
// @ts-expect-error
import HomeView from 'profile/HomeView';
// @ts-expect-error
import ManageOrganizationsView from 'profile/ManageOrganizationsView';
import {OwnablesManageViewOptions} from 'profile/OwnablesManageView';
import ProfileSettingsView from 'profile/ProfileSettingsView';
import ProfileView from 'profile/ProfileView';
import {RouteBundle} from 'routing/RouteConstants';
import LandingViewEnhancements from 'start/LandingViewEnhancements';
import EntityStore from 'stores/EntityStore';
import {renderReactComponent} from 'util/RootReactRender';

export const ProfileRoutes: RouteBundle = {
  domain: 'profile',
  routes: [
    {urlPattern: '', handler: home, legacy: true},
    {urlPattern: ':currentUserUrl', handler: profile, legacy: true},
    {urlPattern: ':currentUserUrl/settings', handler: settings, legacy: true},
    {urlPattern: ':currentUserUrl/organizations/', handler: organizations, legacy: true},
    {urlPattern: ':currentUserUrl/ladders/(:ladderId)', handler: ladders, legacy: true},
    {urlPattern: ':currentUserUrl/enzyme-lists/(:listId)', handler: enzymeLists, legacy: true},
    {urlPattern: ':currentUserUrl/feature-libraries/(:libraryId)', handler: featureLibraries, legacy: true},
  ],
};

function home(): void {
  DatadogUtil.startRUMView('home');
  if (EntityStore.currentUser()) {
    new HomeView().render();
  } else {
    renderReactComponent(LandingViewEnhancements);
  }
}

function profile(): void {
  DatadogUtil.startRUMView('profile');
  new ProfileView().setTabFromHash().render();
}

function settings(): void {
  DatadogUtil.startRUMView('settings');
  new ProfileSettingsView().render();
}

function organizations(): void {
  DatadogUtil.startRUMView('organizations');
  new ManageOrganizationsView().render();
}

function ladders(currentUserUrl: string, ownableId?: string): void {
  DatadogUtil.startRUMView('ladders');
  _ownableView(currentUserUrl, ownableId, 'ladders');
}

function enzymeLists(currentUserUrl: string, ownableId?: string): void {
  DatadogUtil.startRUMView('enzyme_lists');
  _ownableView(currentUserUrl, ownableId, 'enzyme_lists');
}

function featureLibraries(currentUserUrl: string, ownableId?: string): void {
  DatadogUtil.startRUMView('feature_libraries');
  _ownableView(currentUserUrl, ownableId, 'feature_libraries');
}

/**
 * Ownable-view related code below.
 */

interface OwnableViewStore {
  ownableView_ladders?: LaddersView | null;
  ownableView_enzyme_lists?: EnzymeListsView | null;
  ownableView_feature_libraries?: FeatureLibrariesView | null;
}

const ownableViews: OwnableViewStore = {};

function _ownableView(
  _currentUserUrl: string,
  ownableId: string | undefined,
  type: 'ladders' | 'enzyme_lists' | 'feature_libraries'
): void {
  /*
  There's some kind of eslint/typescript bug here. If we don't do the type assertion below, then typescript
  doesn't like `viewName` being used as a key to `ownableViews`. But eslint thinks that the type assertion
  below doesn't narrow the type of `viewName`.
  */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const viewName = `ownableView_${type}` as
    | 'ownableView_ladders'
    | 'ownableView_enzyme_lists'
    | 'ownableView_feature_libraries';
  if (ownableViews[viewName]) {
    ownableViews[viewName].die();
    ownableViews[viewName] = null;
  }
  const navigateToFirstOwnableIfExists = (
    view: typeof LaddersView | typeof EnzymeListsView | typeof FeatureLibrariesView
  ): boolean => {
    if (ownableId == null && view.getBootstrappedOwnables().length > 0) {
      ownableId = view.getBootstrappedOwnables().at(0).id;
      navigateToURL(`${window.location.pathname}${ownableId}`, {replace: true});
      return true;
    } else {
      return false;
    }
  };

  switch (type) {
    case 'ladders':
      if (navigateToFirstOwnableIfExists(LaddersView)) {
        return; // oxlint-disable-line no-useless-return
      }
      ownableViews.ownableView_ladders = new LaddersView({
        ownableId,
      } as OwnablesManageViewOptions<Ladder>).render();
      break;
    case 'enzyme_lists':
      if (navigateToFirstOwnableIfExists(EnzymeListsView)) {
        return; // oxlint-disable-line no-useless-return
      }
      ownableViews.ownableView_enzyme_lists = new EnzymeListsView({
        ownableId,
      } as OwnablesManageViewOptions<EnzymeList>).render();
      break;
    case 'feature_libraries':
      if (navigateToFirstOwnableIfExists(FeatureLibrariesView)) {
        return; // oxlint-disable-line no-useless-return
      }
      ownableViews.ownableView_feature_libraries = new FeatureLibrariesView({
        ownableId,
      } as OwnablesManageViewOptions<FeatureLibrary>).render();
      break;
    default:
      throw new Error(`Unexpected ownable view type: ${type}`);
  }
}
